import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Campaign as CampaignIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Description as DescriptionIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Tag as TagIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { Header } from '../components/Header';
import { AdminBottomNavigation } from '../components/AdminBottomNavigation';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../hooks/useAuth';
import { AdminSidebar } from '../components/AdminSidebar';
import { AdminDesktopSidebar } from '../components/AdminDesktopSidebar';
import { useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';
import { ThemeService } from '../services/themeService';
import { Theme, ThemePost } from '../types/theme';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

export const ThemePostsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [selectedThemeId, setSelectedThemeId] = useState<string>('');
  const [themePosts, setThemePosts] = useState<ThemePost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [themesLoading, setThemesLoading] = useState(false);
  const [isThemeDetailsExpanded, setIsThemeDetailsExpanded] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState<{ [key: string]: boolean }>({});

  // ページネーション用
  const ITEMS_PER_PAGE = 10;
  
  // URLパラメータからページ番号を取得
  const currentPage = useMemo(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  }, [searchParams]);
  
  // ページ番号を変更する関数
  const setCurrentPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }
    navigate(`/admin/theme-posts?${params.toString()}`, { replace: true });
  }, [searchParams, navigate]);

  // ページネーション適用後の投稿リスト
  const paginatedThemePosts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return themePosts.slice(startIndex, endIndex);
  }, [themePosts, currentPage]);

  // 総ページ数
  const totalPages = useMemo(() => {
    return Math.ceil(themePosts.length / ITEMS_PER_PAGE);
  }, [themePosts.length]);

  // アーカイブされていないテーマのみ選択候補に含める（開始日の新しい順にソート）
  const selectableThemes = useMemo(() => {
    const filtered = themes.filter((t) => t.isArchived !== true);
    // 開始日の新しい順にソート
    return filtered.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [themes]);

  useEffect(() => {
    if (company?.id) {
      fetchThemes();
    }
  }, [company?.id]);

  useEffect(() => {
    if (selectedThemeId && company?.id) {
      fetchThemePosts();
    }
  }, [selectedThemeId, company?.id]);

  // テーマ変更時にページをリセット
  const prevSelectedThemeIdRef = useRef<string>('');
  useEffect(() => {
    if (selectedThemeId && prevSelectedThemeIdRef.current !== selectedThemeId) {
      const currentPage = searchParams.get('page');
      if (currentPage && currentPage !== '1') {
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        navigate(`/admin/theme-posts?${params.toString()}`, { replace: true });
      }
      prevSelectedThemeIdRef.current = selectedThemeId;
    }
  }, [selectedThemeId, searchParams, navigate]);

  const fetchThemes = async () => {
    if (!company?.id) return;
    
    setThemesLoading(true);
    try {
      const fetchedThemes = await ThemeService.getThemes(company.id);
      setThemes(fetchedThemes);
      
      // アーカイブされていないテーマの中から初期選択を決定
      const nonArchived = fetchedThemes.filter((t) => t.isArchived !== true);
      const currentSelectedExists = nonArchived.some((t) => t.themeId === selectedThemeId);
      if (!currentSelectedExists) {
        // アクティブなテーマを優先して選択
        const activeTheme = nonArchived.find((t) => t.isActive);
        setSelectedThemeId(activeTheme ? activeTheme.themeId : (nonArchived.length > 0 ? nonArchived[0].themeId : ''));
      }
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    } finally {
      setThemesLoading(false);
    }
  };

  const fetchThemePosts = async () => {
    if (!company?.id || !selectedThemeId) return;
    
    setPostsLoading(true);
    try {
      const fetchedPosts = await ThemeService.getThemePosts(company.id, selectedThemeId);
      setThemePosts(fetchedPosts);
    } catch (error) {
      console.error('テーマ投稿取得エラー:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // ログアウト処理
      navigate('/admin-login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const togglePostExpansion = (postId: string) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'たった今';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}分前`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}時間前`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}日前`;
    return date.toLocaleDateString('ja-JP');
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\//g, '/');
  };

  const selectedTheme = selectableThemes.find(theme => theme.themeId === selectedThemeId);

  if (authLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header 
        onMenuClick={() => setDrawerOpen(true)}
        onSettingsClick={() => navigate('/settings')}
        showAdminBadge={true}
        freeTrialEndDate={company?.isFreeTrial ? company.freeTrialEndDate : undefined}
        companyName={company?.companyName}
        corporateCode={company?.corporateCode}
      />

      {/* モバイル用サイドバー */}
      {!isDesktop && <AdminSidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

      {/* レイアウトコンテナ（ヘッダーの高さ分のpaddingを追加） */}
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* PC版用固定サイドバー */}
        {isDesktop && <AdminDesktopSidebar />}

        {/* メインコンテンツ */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100%', 
          pb: isDesktop ? 2 : 8,
          marginLeft: isDesktop ? '240px' : 0,
        }}>
          {/* 中ヘッダー */}
          <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
              {getBoxTemplate(company?.industryType, 'theme').name}
            </Typography>
          </Box>

          {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
          <Box sx={{ 
            flex: 1,
            p: 2,
            maxWidth: isDesktop ? '1000px' : '100%',
            mx: isDesktop ? 'auto' : 0,
            width: '100%',
          }}>
          <Card sx={{ borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'black',
                  mb: 2,
                }}
              >
                テーマを選択
              </Typography>
              
              {themesLoading ? (
                <CircularProgress size={24} />
              ) : selectableThemes.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${AppColors.border}`,
                    borderRadius: 2,
                    bgcolor: AppColors.surface,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: AppColors.textSecondary,
                      fontSize: 14,
                    }}
                  >
                    テーマがありません
                  </Typography>
                </Box>
              ) : (
                <FormControl fullWidth>
                  <Select
                    value={selectedThemeId}
                    onChange={(e) => {
                      setSelectedThemeId(e.target.value);
                      setIsThemeDetailsExpanded(false);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: 'inherit',
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {selectableThemes.map((theme) => (
                      <MenuItem 
                        key={theme.themeId} 
                        value={theme.themeId}
                        sx={{
                          maxWidth: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                          }}
                        >
                          {theme.title}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
            
            {/* 選択されたテーマの詳細 */}
            {selectedTheme && (
              <>
                <Divider />
                <Box
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onClick={() => setIsThemeDetailsExpanded(!isThemeDetailsExpanded)}
                >
                  <Typography sx={{ color: AppColors.primary, fontWeight: 600, fontSize: 14 }}>
                    テーマ詳細
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ color: AppColors.textSecondary, fontSize: 11, mr: 1 }}>
                      投稿数: {selectedTheme.statistics.totalPosts}
                    </Typography>
                    {isThemeDetailsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </Box>
                </Box>
                
                <Collapse in={isThemeDetailsExpanded}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                      {selectedTheme.title}
                    </Typography>
                    
                    <Box sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
                      {selectedTheme.isActive && (
                        <Chip
                          label="アクティブ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.success,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                          }}
                        />
                      )}
                      {!selectedTheme.isActive && selectedTheme.endDate >= new Date() && (
                        <Chip
                          label="非アクティブ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.textTertiary,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                          }}
                        />
                      )}
                      {!selectedTheme.isActive && selectedTheme.endDate < new Date() && (
                        <Chip
                          label="期限切れ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.textTertiary,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                    
                    <Typography sx={{ color: AppColors.textSecondary, fontSize: 12, mb: 1, whiteSpace: 'pre-line' }}>
                      {selectedTheme.description}
                    </Typography>
                    
                    <Typography sx={{ color: AppColors.textTertiary, fontSize: 11 }}>
                      期間: {formatDate(selectedTheme.startDate)} 〜 {formatDate(selectedTheme.endDate)}
                    </Typography>
                  </Box>
                </Collapse>
              </>
            )}
          </Card>
          
          {/* 投稿一覧 */}
          {!selectedThemeId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
              <DescriptionIcon sx={{ fontSize: 64, color: AppColors.textTertiary, mb: 2 }} />
              <Typography sx={{ color: AppColors.textSecondary, fontSize: 16 }}>
                テーマを選択してください
              </Typography>
            </Box>
          ) : postsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : themePosts.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
              <InboxIcon sx={{ fontSize: 64, color: AppColors.textTertiary, mb: 2 }} />
              <Typography sx={{ color: AppColors.textSecondary, fontSize: 16 }}>
                このテーマの投稿はありません
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2, pb: 2 }}>
            {paginatedThemePosts.map((post) => {
              const isExpanded = expandedPosts[post.id] || false;
              const content = post.processedContent || post.content;
              const needsExpansion = content.length > 100 && !isDesktop;
              
              return (
                <Card 
                  key={post.id} 
                  sx={{ 
                    mb: 1.5, 
                    borderRadius: 3, 
                    border: `1px solid ${AppColors.border}`
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Chip
                        label="テーマ"
                        size="small"
                        sx={{
                          bgcolor: AppColors.withOpacity(AppColors.primary, 0.1),
                          color: AppColors.primary,
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      />
                    </Box>
                    
                    <Typography
                      sx={{ 
                        fontSize: 14, 
                        lineHeight: 1.6, 
                        color: AppColors.textPrimary, 
                        mb: needsExpansion ? 1 : 0,
                        whiteSpace: 'pre-line'
                      }}
                      style={{
                        display: isDesktop ? 'block' : '-webkit-box',
                        WebkitLineClamp: isDesktop ? 'none' : (isExpanded ? 'none' : 3),
                        WebkitBoxOrient: 'vertical',
                        overflow: isDesktop ? 'visible' : 'hidden',
                      }}
                    >
                      {content}
                    </Typography>
                    
                    {needsExpansion && (
                      <Button
                        onClick={(e) => { e.stopPropagation(); togglePostExpansion(post.id); }}
                        sx={{
                          color: AppColors.primary,
                          fontSize: 13,
                          fontWeight: 600,
                          p: 0.5,
                          minWidth: 'auto',
                        }}
                      >
                        {isExpanded ? '表示を減らす' : 'さらに表示'}
                      </Button>
                    )}
                    
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center' }}>
                      {post.tags.length > 0 && (
                        <>
                          <TagIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                          <Typography
                            sx={{
                              color: AppColors.textSecondary,
                              fontSize: 12,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {post.tags.join(', ')}
                          </Typography>
                        </>
                      )}
                      <Typography sx={{ color: AppColors.textTertiary, fontSize: 12 }}>
                        {formatTimeAgo(post.createdAt)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* ページネーション */}
            {totalPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2,
                p: 3,
                pt: 2,
              }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: AppColors.textSecondary,
                    mb: 1,
                  }}
                >
                  {themePosts.length}件中 {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, themePosts.length)}件を表示
                </Typography>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={postsLoading}
                />
              </Box>
            )}
            </Box>
          )}
          </Box>
        </Box>

        {/* モバイル版のみボトムナビゲーション表示 */}
        {!isDesktop && <AdminBottomNavigation currentTab="theme" />}
      </Box>
    </Box>
  );
};
